<template>
  <div
    class="flex-1 min-w-0 flex flex-col min-h-0 bg-gray-50 dark:bg-[#0d1117] relative"
    @dragover.prevent
    @drop.prevent="$emit('drop')"
  >
    <FlashNotifications ref="flash" />
    <Transition name="drop-hint">
      <div v-if="dragActive" class="absolute inset-3 z-10 rounded-2xl border-2 border-dashed border-blue-400 dark:border-blue-500 bg-blue-50/70 dark:bg-blue-900/20 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
        <div class="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold text-sm">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z"/>
          </svg>
          Drop to add to your stack
        </div>
      </div>
    </Transition>

    <div class="flex-shrink-0 sticky top-0 z-[5] bg-gray-50/95 dark:bg-[#0d1117]/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6">
      <div class="flex items-center justify-between gap-3 h-14">
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 17.25v-.228a4.5 4.5 0 0 0-.12-1.03l-2.268-9.64a3.375 3.375 0 0 0-3.285-2.602H7.923a3.375 3.375 0 0 0-3.285 2.602l-2.268 9.64a4.5 4.5 0 0 0-.12 1.03v.228m19.5 0a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3m19.5 0a3 3 0 0 0-3-3H5.25a3 3 0 0 0-3 3m16.5 0h.008v.008h-.008v-.008Zm-3 0h.008v.008h-.008v-.008Z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none truncate">IT Components</h1>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none truncate">{{ summaryLabel }} in your organization's stack</p>
          </div>
        </div>
        <button type="button" @click="openAddCustom()"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors shadow-sm flex-shrink-0">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
          </svg>
          <span class="hidden sm:inline">Add Component</span>
          <span class="sm:hidden">Add</span>
        </button>
      </div>

      <div class="pb-3 flex items-center gap-2">
        <div class="relative flex-1 min-w-0">
          <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="searchInput" type="text" placeholder="Search by name or category"
            class="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1C2128] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
          <button v-if="searchInput" type="button" @click="searchInput = ''" title="Clear search"
            class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <SortFilterButton :options="SORT_OPTIONS" :model-value="currentSortValue" @update:model-value="applySort" />
        <CategoryFilterButton :categories="availableCategories" :selected="store.filters.type"
          @toggle="store.toggleType($event)" @clear="store.clearTypeFilters()" />
      </div>
    </div>

    <div class="flex-1 overflow-y-auto py-6 px-4 sm:px-6">
      <div v-if="store.loading && store.components.length === 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5 animate-pulse">
        <div v-for="i in skeletonCount" :key="i" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 p-4">
          <div class="flex items-start gap-2.5 mb-3">
            <div class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
            <div class="flex-1 space-y-1.5 pt-0.5">
              <div class="h-3.5 w-2/3 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div class="h-2.5 w-1/2 bg-gray-100 dark:bg-gray-800 rounded"></div>
            </div>
          </div>
          <div class="flex items-center justify-between gap-2">
            <div class="h-4 w-16 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
            <div class="h-3 w-14 bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>

      <div v-else-if="store.error" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-24 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
        <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors">Try again</button>
      </div>

      <div v-else-if="store.components.length === 0 && store.hasActiveFilters" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-20 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
        </div>
        <p class="text-base font-semibold text-gray-900 dark:text-white mb-1">No matching components</p>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">Try a different search term or category.</p>
        <button @click="clearSearch" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors">Clear filters</button>
      </div>

      <div v-else-if="store.components.length === 0" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-20 text-center px-4">
        <div class="w-20 h-20 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-6">
          <svg class="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z"/>
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Your stack is empty</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-2">Drag a technology from the catalog, or click the + on hover, to add it here.</p>
      </div>

      <TransitionGroup v-else name="org-card" tag="div" @before-leave="lockCardSize"
        class="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
        <div v-for="component in store.components" :key="component._key || component.id"
          draggable="true"
          @dragstart="onDragStart($event, component)"
          @dragend="draggingId = null; $emit('drag-end')"
          @click="openEdit(component)"
          :class="[draggingId === component.id ? 'opacity-30' : '', highlightedId === component.id ? 'card-highlight' : '']"
          class="relative bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md hover:-translate-y-0.5 hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-200 cursor-grab active:cursor-grabbing">
          <div class="flex items-start gap-2.5 mb-3">
            <ItComponentLogo :name="component.name" :logo-url="component.logo_url" size="w-8 h-8" initial-text-size="text-xs" />
            <div class="flex-1 min-w-0 pt-0.5">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight">{{ component.name }}</h3>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ component.vendor || 'No vendor listed' }}</p>
            </div>
          </div>
          <div class="flex items-center justify-between gap-2">
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium" :class="typeBadgeClass(component.type)">
              {{ typeLabel(component.type) }}
            </span>
            <span class="inline-flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 3.75c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"/>
              </svg>
              {{ component.versions_count || 0 }} {{ component.versions_count === 1 ? 'version' : 'versions' }}
            </span>
          </div>
        </div>
      </TransitionGroup>

      <div ref="sentinel" class="h-8"></div>
      <div v-if="store.loadingMore" class="flex justify-center py-4">
        <div class="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </div>

    <AddItComponentSlideOver
      :open="modal.open"
      :organization-id="orgId"
      :title="modal.title"
      :submit-label="modal.editing ? 'Save' : 'Add'"
      :saving="modal.saving"
      :error="modal.error"
      :initial-name="modal.editing?.name || modal.prefillName || ''"
      :initial-type="modal.editing?.type || modal.prefillType || ''"
      :initial-vendor="modal.editing?.vendor || modal.prefillVendor || ''"
      :initial-description="modal.editing?.description || modal.prefillDescription || ''"
      :org-component-id="modal.editing?.id || ''"
      :global-component-id="modal.editing?.global_component_id || ''"
      :highlight-versions="!!modal.editing && justCreatedComponentId === modal.editing.id"
      :created-at="modal.editing?.created_at || ''"
      :added-by-name="modal.editing?.added_by_name || ''"
      @close="closeModal"
      @submit="submitModal"
      @delete="deleteFromSlideOver"
      @notify="notify" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useOrgComponentsStore } from 'stores/orgComponents.js'
import { useOrgComponentOptionsStore } from 'stores/orgComponentOptions.js'
import { getCachedItComponentsCount } from 'composables/useItComponentsCountCache.js'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import AddItComponentSlideOver from 'controllers/vue_components/organizations/it_components/AddItComponentSlideOver.vue'
import CategoryFilterButton from 'controllers/vue_components/organizations/it_components/CategoryFilterButton.vue'
import SortFilterButton from 'controllers/vue_components/organizations/it_components/SortFilterButton.vue'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import { TYPE_OPTIONS, SORT_OPTIONS, typeLabel, typeBadgeClass } from 'controllers/vue_components/organizations/it_components/itComponentOptions.js'

const props = defineProps({
  orgId: { type: String, required: true },
  dragActive: { type: Boolean, default: false },
})

const emit = defineEmits(['drop', 'drag-start', 'drag-end'])

const store = useOrgComponentsStore()
const componentOptionsStore = useOrgComponentOptionsStore()
// Read synchronously at setup time so the very first render already shows
// the right number of skeleton cards (from a prior visit to this org), no
// flash of a generic count once the real fetch resolves. Capped so an
// organization with a very large stack can't render an absurdly tall skeleton
// (the real list still loads in full — this only bounds the placeholder).
const skeletonCount = ref(Math.min(getCachedItComponentsCount(props.orgId), 24))
const flash = ref(null)
const draggingId = ref(null)
const highlightedId = ref(null)
let highlightTimeout = null
// Set right after a fresh Add succeeds (see submitModal) so the slide-over's
// Versions section can call out that it's now usable, since the panel stays
// open at that point rather than closing — see submitModal's own comment.
// Cleared on a timeout, or naturally on the next open since a new modal
// object always replaces this one.
const justCreatedComponentId = ref(null)
let justCreatedTimeout = null

function onDragStart(event, component) {
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', component.name)
  draggingId.value = component.id
  emit('drag-start', component)
}

// Briefly rings a card so a just-added component is easy to spot in the grid
// — cleared automatically after the highlight animation has had time to play.
function highlightRecent(id) {
  clearTimeout(highlightTimeout)
  highlightedId.value = id
  highlightTimeout = setTimeout(() => { highlightedId.value = null }, 3500)
}

// Fires while the leaving card is still in normal grid flow, just before the
// CSS classes that switch it to position: absolute are applied. Without
// this, taking it out of flow collapses its box to its (positioned)
// ancestor's width instead of its actual grid-column width, so it visibly
// snaps to a different size for a frame right as the fade starts.
function lockCardSize(el) {
  const { width, height } = el.getBoundingClientRect()
  el.style.width = `${width}px`
  el.style.height = `${height}px`
}

const summaryLabel = computed(() => {
  const count = store.pageInfo.total_records
  return `${count} ${count === 1 ? 'component' : 'components'}`
})

// Only the categories actually present among this org's own components
// (not the full TYPE_OPTIONS list), in TYPE_OPTIONS' own order so the list
// stays stable/predictable rather than reshuffling by count on every add
// or remove.
const availableCategories = computed(() => {
  const counts = {}
  componentOptionsStore.components.forEach((c) => { counts[c.type] = (counts[c.type] || 0) + 1 })
  return TYPE_OPTIONS
    .filter((option) => counts[option.value])
    .map((option) => ({ value: option.value, label: option.label, count: counts[option.value] }))
})

// SortFilterButton is single-select and keys off one combined "value" string
// rather than the store's separate by/order fields.
const currentSortValue = computed(() => `${store.sort.by}-${store.sort.order}`)

async function applySort(value) {
  const option = SORT_OPTIONS.find((o) => o.value === value)
  if (option) await store.setSort(option.by, option.order)
}

const searchInput = ref('')
const sentinel = ref(null)
const modal = ref({ open: false, editing: null, title: '', saving: false, error: '', prefillName: '', prefillType: '', prefillVendor: '', prefillDescription: '' })
let searchDebounce = null
let observer = null

watch(searchInput, (value) => {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => store.setSearch(value.trim()), 300)
})

onMounted(async () => {
  await store.init(props.orgId)
  searchInput.value = store.filters.search
  componentOptionsStore.fetchIfNeeded(props.orgId)

  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) store.fetchMore()
  }, { threshold: 0.1 })
  if (sentinel.value) observer.observe(sentinel.value)
})

onUnmounted(() => {
  observer?.disconnect()
  clearTimeout(searchDebounce)
  clearTimeout(highlightTimeout)
  clearTimeout(justCreatedTimeout)
})

async function retry() {
  await store.fetchPage(true)
}

async function clearSearch() {
  searchInput.value = ''
  await store.clearFilters()
}

function openEdit(component) {
  modal.value = { open: true, editing: component, title: 'Edit IT Component', saving: false, error: '', prefillName: '', prefillType: '', prefillVendor: '', prefillDescription: '' }
}

function closeModal() {
  modal.value.open = false
}

function openAddCustom(prefillName = '') {
  modal.value = { open: true, editing: null, title: 'Add IT Component', saving: false, error: '', prefillName, prefillType: '', prefillVendor: '', prefillDescription: '' }
}

// Dragging (or clicking "+" on) a catalog row no longer adds it straight
// away — it opens the slide-over prefilled from the catalog entry so the
// user can review/adjust type & vendor before anything is created. Nothing
// is sent to the backend, and the component doesn't appear on this side,
// until the user actually clicks Save; Cancel leaves the org's stack
// untouched. See AddItComponentSlideOver's `open` watcher for why this
// doesn't autofocus/open the name dropdown the way a blank Add does.
function openAddFromComponent(component) {
  modal.value = {
    open: true,
    editing: null,
    title: 'Add IT Component',
    saving: false,
    error: '',
    prefillName: component.name,
    prefillType: component.type || '',
    prefillVendor: component.vendor || '',
    prefillDescription: component.description || '',
  }
}

function actionErrorMessage(res) {
  const validationErrors = res.error?.data?.data
  if (Array.isArray(validationErrors) && validationErrors.length) return validationErrors.join(', ')
  return 'Something went wrong. Please try again.'
}

async function submitModal(payload) {
  modal.value.saving = true
  modal.value.error = ''

  const { editing } = modal.value
  const res = editing ? await store.updateComponent(editing.id, payload) : await store.addComponent(payload)

  modal.value.saving = false

  if (!res.js_error && res.response.success) {
    if (editing) {
      closeModal()
      flash.value?.show('IT component updated successfully.', 'notice')
    } else {
      // Stay open, switched into edit mode for the newly created component —
      // this is what lets the slide-over's Versions section appear (it needs
      // a real org_component id) so the user can start adding versions right
      // away instead of having to close and reopen via the card's own Edit
      // button. Closing from here on is a deliberate Cancel/X, not an
      // automatic side effect of the first Save.
      modal.value.editing = res.response.data
      modal.value.title = 'Edit IT Component'
      highlightRecent(res.response.data.id)
      flash.value?.show('IT component added successfully.', 'notice')

      clearTimeout(justCreatedTimeout)
      justCreatedComponentId.value = res.response.data.id
      justCreatedTimeout = setTimeout(() => { justCreatedComponentId.value = null }, 6000)
    }
    refreshCategoryOptions()
  } else {
    modal.value.error = actionErrorMessage(res)
  }
}

async function deleteFromSlideOver(id) {
  closeModal()

  const res = await store.deleteComponent(id)

  if (!res.js_error && res.response.success) {
    flash.value?.show('IT component removed successfully.', 'notice')
    refreshCategoryOptions()
  } else {
    flash.value?.show(actionErrorMessage(res), 'alert')
  }
}

// Called after any mutation that can change which categories exist in the
// org's stack (or their counts) — this page's own modal add/edit/delete
// flows, and (via defineExpose below) index.vue's drag-and-drop add/remove
// flows too. componentOptionsStore is a cached, capped-at-500 list normally
// only refetched on its own 5-minute TTL, so without this the category
// dropdown would show stale options/counts right after a change made on
// this very page.
function refreshCategoryOptions() {
  componentOptionsStore.invalidate()
  componentOptionsStore.fetchIfNeeded(props.orgId)
}

function notify(message, type) {
  flash.value?.show(message, type)
}

defineExpose({
  notify,
  openAddCustom,
  openAddFromComponent,
  highlightRecent,
  refreshCategoryOptions,
})
</script>

<style scoped>
/* A category/search filter change swaps most (often all) of the grid at
   once via a single store.components reassignment — not a one-off add or
   remove, so the transition needs to hold up when many cards enter/leave
   simultaneously, not just for a single card:
   - .org-card-move: FLIP transition for cards that stay but shift position.
   - leave-active taken out of flow (absolute) so fading-out cards don't
     shove the remaining grid around while they're still visible — without
     this, remaining cards visibly jump the instant the array is replaced,
     which read as glitchy rather than intentional.
   - enter/leave kept to a plain fade + small vertical drift, no scale —
     the previous scale(0.92) bounce compounded across a dozen simultaneous
     cards read as a flashy "pop" rather than a calm filter transition. */
.org-card-move {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.org-card-enter-active {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}
.org-card-leave-active {
  transition: opacity 0.15s ease-in;
  position: absolute;
}
.org-card-enter-from {
  opacity: 0;
  transform: translateY(4px);
}
.org-card-leave-to {
  opacity: 0;
}

.card-highlight {
  animation: card-highlight-pulse 3.5s ease-out;
}
@keyframes card-highlight-pulse {
  0% {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.55);
    background-color: rgba(37, 99, 235, 0.07);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
    background-color: transparent;
  }
}

.drop-hint-enter-active,
.drop-hint-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.drop-hint-enter-from,
.drop-hint-leave-to {
  opacity: 0;
  transform: scale(0.97);
}
</style>
